// The users and circles pages' pure helpers: labels, the search as sent and
// the typed deletion check. No DOM.
import type { AdminSchemas } from "@10twenty/api/admin";

export type CircleKind = AdminSchemas["CircleKind"];
export type UserDevice = AdminSchemas["UserDevice"];

/** The most a search holds, in code points. */
export const MAX_SEARCH = 254;

export const KIND_LABELS: Record<CircleKind, string> = { family: "Family", friends: "Friends", event: "Event" };

export const PLATFORM_LABELS: Record<UserDevice["platform"], string> = {
  android: "Android",
  ios: "iPhone",
  web: "Web",
};

const PERMISSION_LABELS: Record<NonNullable<UserDevice["location_permission"]>, string> = {
  always_precise: "Always, precise",
  always_approximate: "Always, approximate",
  foreground_only: "While in use",
  denied: "Denied",
};

/** `text` cut to `MAX_SEARCH` code points. */
export function clipSearch(text: string): string {
  const points = [...text];
  return points.length > MAX_SEARCH ? points.slice(0, MAX_SEARCH).join("") : text;
}

/** The search as sent: trimmed, left out when blank. */
export function searchParams(q: string): { q?: string } {
  const trimmed = q.trim();
  return trimmed ? { q: trimmed } : {};
}

/** The device's location setting as a short phrase ("—" when it never reported one). */
export function locationLabel(device: Pick<UserDevice, "location_enabled" | "location_permission">): string {
  const parts = [];
  if (device.location_enabled !== null) parts.push(device.location_enabled ? "On" : "Off");
  if (device.location_permission) parts.push(PERMISSION_LABELS[device.location_permission]);
  return parts.length ? parts.join(" · ") : "—";
}

/** Whether the typed confirmation repeats the account's address (as the server compares it). */
export function deletionConfirmed(typed: string, email: string): boolean {
  return typed.trim().toLowerCase() === email.toLowerCase();
}

export function reportsLabel(counts: AdminSchemas["ReportCounts"]): string {
  if (counts.total === 0) return "None";
  return counts.open ? `${counts.total} (${counts.open} open)` : String(counts.total);
}

/** A timestamp for a table cell, "—" when absent. */
export function when(value: string | null): string {
  return value ? new Date(value).toLocaleString() : "—";
}
